import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { api, errorMessage, unwrap } from "../api/client";
import { FormattedMessage } from "react-intl";
import { Alert, Spinner } from "../components/ui";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { formatValue } from "../lib/plans";
import { cohortSeries, filterLimits, filterPlanRows, type LimitFilter, type LimitRow, type PlanFilter, type PlanRow } from "./library";
import { readPref, writePref } from "./prefs";

type Cohort = { label: string; price: number; churn: number; short: string };
type Stream = { label: string; k10: [number, number]; k50: [number, number] };
type LimitsData = { limit_kinds: Record<string, string>; limits: LimitRow[] };

/** The data of an ECLIPSE page (eclipse.json, eclipse-r2.json). Revision 1 carries its own limits table, cohorts
 * and streams; revision 2 reads the limits live from the plan catalogue and only names the kinds (`plan_kinds`). */
export interface EclipseData {
  limit_kinds?: Record<string, string>;
  limits?: LimitRow[];
  cohorts?: Cohort[];
  streams?: Stream[];
  plan_kinds?: Record<string, string>;
}

const PLANS = [
  ["free", "Free"],
  ["pro", "Pro"],
  ["business", "Business"],
  ["enterprise", "Enterprise"],
] as const;
type Plan = (typeof PLANS)[number][0];
const SERIES = ["var(--lib-s1)", "var(--lib-s2)", "var(--lib-s3)", "var(--lib-s4)"];

/** The two days of ECLIPSE's limit changes (12 and 13 October 2026), in the reader's way. */
const OCT_12 = new Date(Date.UTC(2026, 9, 12, 12));
const OCT_13 = new Date(Date.UTC(2026, 9, 13, 12));

/** Dollars in the reader's locale, compact ("$12K", "$1.2M"): the charts' axes and tips. */
function useCompactUsd(): (value: number, digits?: number) => string {
  const intl = useIntlShape();
  return (value, digits = 1) => intl.formatNumber(value, { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: digits });
}

/** The interactive parts of the ECLIPSE pages, by mount name (`data-library-mount` in eclipse.html and
 * eclipse-r2.html); a page gets only the views its data has. */
export function eclipseMounts(data: EclipseData): Record<string, ReactNode> {
  const mounts: Record<string, ReactNode> = { plans: <PlanLimitsExplorer kinds={data.plan_kinds ?? {}} /> };
  if (data.limits && data.limit_kinds) mounts.limits = <LimitsExplorer data={{ limits: data.limits, limit_kinds: data.limit_kinds }} />;
  if (data.cohorts) mounts.cohort = <CohortChart data={{ cohorts: data.cohorts }} />;
  if (data.streams) mounts.money = <MoneyChart data={{ streams: data.streams }} />;
  return mounts;
}

function savedFilter(): LimitFilter & { plan: "" | Plan } {
  const empty = { q: "", area: "", kind: "", changed: "" as const, plan: "" as const };
  try {
    return { ...empty, ...JSON.parse(readPref("lib.eclipse.filters") || "{}") };
  } catch {
    return empty;
  }
}

export function LimitsExplorer({ data }: { data: LimitsData }) {
  const t = useT();
  const intl = useIntlShape();
  const day = (d: Date) => intl.formatDate(d, { day: "numeric", month: "short", timeZone: "UTC" });
  const [f, setF] = useState(savedFilter);
  const update = (patch: Partial<typeof f>) => {
    const next = { ...f, ...patch };
    setF(next);
    writePref("lib.eclipse.filters", JSON.stringify(next));
  };
  const areas = useMemo(() => [...new Set(data.limits.map((r) => r.area))], [data.limits]);
  const rows = useMemo(() => filterLimits(data.limits, f), [data.limits, f]);
  const plans = PLANS.filter(([k]) => !f.plan || f.plan === k);
  return (
    <div className="stack">
      <div className="lib-filters" role="search">
        <label>
          {t("library.filter.search")}
          <input type="search" value={f.q} onChange={(e) => update({ q: e.target.value })} placeholder={t("library.limits.searchPlaceholder")} dir="auto" />
        </label>
        <label>
          {t("library.filter.area")}
          <select value={f.area} onChange={(e) => update({ area: e.target.value })}>
            <option value="">{t("library.filter.allAreas")}</option>
            {areas.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </label>
        <label>
          {t("library.limits.plan")}
          <select value={f.plan} onChange={(e) => update({ plan: e.target.value as Plan | "" })}>
            <option value="">{t("library.limits.allPlans")}</option>
            {PLANS.map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          {t("library.filter.kind")}
          <select value={f.kind} onChange={(e) => update({ kind: e.target.value })}>
            <option value="">{t("library.filter.allKinds")}</option>
            <option value="lever">{t("library.limits.kind.lever")}</option>
            <option value="abuse">{t("library.limits.kind.abuse")}</option>
            <option value="free">{t("library.limits.kind.free")}</option>
            <option value="comp">{t("library.limits.kind.comp")}</option>
          </select>
        </label>
        <label>
          {t("library.limits.changed")}
          <select value={f.changed} onChange={(e) => update({ changed: e.target.value as LimitFilter["changed"] })}>
            <option value="">{t("library.limits.allRows")}</option>
            <option value="1">{t("library.limits.changedBoth", { range: intl.formatDateTimeRange(OCT_12, OCT_13, { day: "numeric", month: "short", timeZone: "UTC" }) })}</option>
            <option value="2">{t("library.limits.changedOnly", { date: day(OCT_13) })}</option>
          </select>
        </label>
        <span className="lib-count" aria-live="polite">{t("library.limits.count", { shown: rows.length, total: data.limits.length })}</span>
      </div>
      <div className="lib-table-wrap tall">
        <table className="lib-table lib-limits" style={{ minWidth: f.plan ? 900 : 1100 }}>
          <thead>
            <tr>
              <th>{t("library.filter.area")}</th>
              <th>{t("library.limits.th.limit")}</th>
              {plans.map(([k, label]) => (
                <th key={k}>{label}</th>
              ))}
              <th>{t("library.filter.kind")}</th>
              <th>{t("library.limits.th.enforced")}</th>
              <th>{t("library.limits.th.unitCost")}</th>
              <th className="num">{t("library.limits.th.worstFree")}</th>
              <th>{t("library.limits.th.today")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.area}-${r.name}-${i}`} className={r.changed ? "chg" : undefined}>
                <td className="area">{r.area}</td>
                <td className="nm">
                  {r.name} {r.changed ? <span className="p-c">{day(r.changed === 2 ? OCT_13 : OCT_12)}</span> : null}
                </td>
                {plans.map(([k]) => (
                  <td key={k}>{r[k]}</td>
                ))}
                <td>
                  <span className={`kind k-${r.kind}`}>{data.limit_kinds[r.kind] ?? r.kind}</span>
                </td>
                <td>{r.enforced}</td>
                <td>{r.unit_cost}</td>
                <td className="num">{r.worst_free}</td>
                <td>{r.today}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PLAN_COLUMNS = ["free", "pro", "business", "enterprise"] as const;

function savedPlanFilter(): PlanFilter {
  const empty: PlanFilter = { q: "", area: "", kind: "", only: "" };
  try {
    return { ...empty, ...JSON.parse(readPref("lib.eclipse2.filters") || "{}") };
  } catch {
    return empty;
  }
}

/** ECLIPSE revision 2's limits explorer: every item of the plan catalogue with each plan's value as it is now
 * (GET /v1/admin/plans: the ECLIPSE defaults of domain/plans.py with platform admins' edits over them). Nothing is
 * copied into the page, so it never goes stale. */
export function PlanLimitsExplorer({ kinds }: { kinds: Record<string, string> }) {
  const t = useT();
  const intl = useIntlShape();
  const catalogue = useQuery({ queryKey: ["admin-plans"], queryFn: () => unwrap(api.GET("/v1/admin/plans")) });
  const [f, setF] = useState(savedPlanFilter);
  const update = (patch: Partial<PlanFilter>) => {
    const next = { ...f, ...patch };
    setF(next);
    writePref("lib.eclipse2.filters", JSON.stringify(next));
  };
  const rows = useMemo<PlanRow[]>(() => {
    const data = catalogue.data;
    if (!data) return [];
    const plans = PLAN_COLUMNS.map((k) => data.plans.find((p) => p.key === k));
    return data.items.map((item) => ({
      key: item.key,
      area: item.area,
      label: item.label,
      kind: item.kind,
      description: item.description ?? "",
      published: item.published,
      values: Object.fromEntries(plans.map((p, i) => [PLAN_COLUMNS[i], p ? formatValue(item, p.values[item.key], { t, locale: intl.locale }) : "–"])),
      edited: plans.filter((p) => p && JSON.stringify(p.values[item.key] ?? null) !== JSON.stringify(p.defaults[item.key] ?? null)).map((p) => p!.key),
      off: plans.filter((p) => p?.disabled.includes(item.key)).map((p) => p!.key),
    }));
  }, [catalogue.data, t, intl.locale]);
  const areas = useMemo(() => [...new Set(rows.map((r) => r.area))], [rows]);
  const shown = useMemo(() => filterPlanRows(rows, f), [rows, f]);
  if (catalogue.isLoading) return <Spinner />;
  if (catalogue.error || !catalogue.data) return <Alert tone="danger">{errorMessage(catalogue.error)}</Alert>;
  const names = Object.fromEntries(catalogue.data.plans.map((p) => [p.key, p.name]));
  return (
    <div className="stack">
      <div className="lib-filters" role="search">
        <label>
          {t("library.filter.search")}
          <input type="search" value={f.q} onChange={(e) => update({ q: e.target.value })} placeholder={t("library.plans.searchPlaceholder")} dir="auto" />
        </label>
        <label>
          {t("library.filter.area")}
          <select value={f.area} onChange={(e) => update({ area: e.target.value })}>
            <option value="">{t("library.filter.allAreas")}</option>
            {areas.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </label>
        <label>
          {t("library.filter.kind")}
          <select value={f.kind} onChange={(e) => update({ kind: e.target.value })}>
            <option value="">{t("library.filter.allKinds")}</option>
            {Object.entries(kinds).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          {t("library.plans.show")}
          <select value={f.only} onChange={(e) => update({ only: e.target.value as PlanFilter["only"] })}>
            <option value="">{t("library.plans.every")}</option>
            <option value="published">{t("library.plans.published")}</option>
            <option value="edited">{t("library.plans.edited")}</option>
          </select>
        </label>
        <span className="lib-count" aria-live="polite">{t("library.plans.count", { shown: shown.length, total: rows.length })}</span>
      </div>
      <div className="lib-table-wrap tall">
        <table className="lib-table lib-limits" style={{ minWidth: 1080 }}>
          <thead>
            <tr>
              <th>{t("library.filter.area")}</th>
              <th>{t("library.plans.th.item")}</th>
              {PLAN_COLUMNS.map((k) => (
                <th key={k}>{names[k] ?? k}</th>
              ))}
              <th>{t("library.filter.kind")}</th>
              <th>{t("library.plans.th.meaning")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.key} className={r.edited.length ? "chg" : undefined}>
                <td className="area">{r.area}</td>
                <td className="nm">
                  {r.label} {r.published ? <span className="p-pub" title={t("library.plans.publishedTitle")}>{t("library.plans.publishedBadge")}</span> : null}
                  {r.edited.length ? (
                    <span className="p-c" title={t("library.plans.editedTitle", { plans: intl.formatList(r.edited.map((k) => names[k] ?? k)) })}>
                      {t("library.plans.editedBadge")}
                    </span>
                  ) : null}
                  <div className="lib-key">{r.key}</div>
                </td>
                {PLAN_COLUMNS.map((k) => (
                  <td key={k}>
                    {r.values[k]}
                    {r.off.includes(k) ? <div className="lib-off">{t("library.plans.notEnforced")}</div> : null}
                  </td>
                ))}
                <td>
                  <span className={`kind k-${r.kind}`}>{kinds[r.kind] ?? r.kind}</span>
                </td>
                <td className="basis">{r.description || "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="lib-small">
        <FormattedMessage
          id="library.plans.foot"
          values={{ link: (c) => <a href="/admin/plans">{c}</a>, code: (c) => <code>{c}</code>, api: "GET /v1/admin/plans", file: "domain/plans.py" }}
        />
      </p>
    </div>
  );
}

function useTip() {
  const box = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number; body: ReactNode } | null>(null);
  const show = (svg: SVGSVGElement, W: number, H: number, x: number, y: number, body: ReactNode) => {
    const r = svg.getBoundingClientRect();
    const b = box.current?.getBoundingClientRect();
    if (!b) return;
    const left = Math.max(80, Math.min(b.width - 80, r.left - b.left + (x * r.width) / W));
    setTip({ x: left, y: r.top - b.top + (y * r.height) / H, body });
  };
  const node = tip && (
    <div className="lib-tip" style={{ left: tip.x, top: tip.y }}>
      {tip.body}
    </div>
  );
  return { box, show, hide: () => setTip(null), node };
}

export function CohortChart({ data }: { data: { cohorts: Cohort[] } }) {
  const t = useT();
  const compactUsd = useCompactUsd();
  const series = useMemo(() => data.cohorts.map((c) => cohortSeries(c.price, c.churn)), [data.cohorts]);
  const [hover, setHover] = useState<number | null>(null);
  const { box, show, hide, node } = useTip();
  const W = 760, H = 320, L = 56, R = 12, T = 14, B = 34, max = 350000;
  const x = (i: number) => L + ((W - L - R - 150) * i) / 35;
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  // End labels, spread so they never overlap.
  const ends = series.map((s, i) => ({ i, v: s[35] })).sort((a, b) => b.v - a.v);
  const labelY: Record<number, number> = {};
  let last = -99;
  for (const e of ends) {
    let yy = y(e.v) + 4;
    if (yy - last < 15) yy = last + 15;
    labelY[e.i] = yy;
    last = yy;
  }
  const onMove = (ev: PointerEvent<SVGRectElement>) => {
    const svg = ev.currentTarget.ownerSVGElement!;
    const r = svg.getBoundingClientRect();
    const sx = ((ev.clientX - r.left) * W) / r.width;
    const i = Math.max(0, Math.min(35, Math.round(((sx - L) / (x(35) - L)) * 35)));
    setHover(i);
    show(svg, W, H, x(i), y(series[0][i]), (
      <>
        <b>{t("library.chart.month", { month: i + 1 })}</b>
        {data.cohorts.map((c, j) => (
          <div key={c.label}>{c.label.split(",")[0]}: {compactUsd(series[j][i])}</div>
        ))}
      </>
    ));
  };
  return (
    <div className="lib-chart" ref={box}>
      <h4>{t("library.cohort.title")}</h4>
      <p className="cap">
        <FormattedMessage id="library.cohort.caption" values={{ est: (c) => <span className="est">{c}</span> }} />
      </p>
      <div className="lib-legend">
        {data.cohorts.map((c, i) => (
          <span key={c.label}>
            <i style={{ background: SERIES[i] }} />
            {c.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t("library.cohort.aria")}>
        {Array.from({ length: 8 }, (_, k) => k * 50000).map((v) => (
          <g key={v}>
            <line x1={L} x2={x(35)} y1={y(v)} y2={y(v)} stroke="var(--lib-grid)" />
            <text x={L - 8} y={y(v) + 4} textAnchor="end">{compactUsd(v)}</text>
          </g>
        ))}
        {[1, 12, 24, 36].map((m) => (
          <text key={m} x={x(m - 1)} y={H - 12} textAnchor="middle">{t("library.chart.month", { month: m })}</text>
        ))}
        {series.map((s, i) => (
          <g key={i}>
            <path d={s.map((v, j) => `${j ? "L" : "M"}${x(j).toFixed(1)},${y(v).toFixed(1)}`).join("")} fill="none" stroke={SERIES[i]} strokeWidth={2} strokeLinejoin="round" />
            <circle cx={x(35)} cy={y(s[35])} r={4} fill={SERIES[i]} stroke="var(--surface)" strokeWidth={2} />
            <text x={x(35) + 9} y={labelY[i]} style={{ fontWeight: 600 }}>{compactUsd(s[35])} · {data.cohorts[i].short}</text>
          </g>
        ))}
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} stroke="var(--muted)" />}
        <rect x={L} y={T} width={x(35) - L} height={H - T - B} fill="transparent" onPointerMove={onMove} onPointerLeave={() => { setHover(null); hide(); }} />
      </svg>
      {node}
    </div>
  );
}

export function MoneyChart({ data }: { data: { streams: Stream[] } }) {
  const t = useT();
  const intl = useIntlShape();
  const millions = useCompactUsd();
  const m$ = (v: number) => millions(v * 1e6);
  const installers = (k: number) => intl.formatNumber(k * 1000, { notation: "compact" });
  const [base, setBase] = useState<10 | 50>(10);
  const { box, show, hide, node } = useTip();
  const W = 760, H = 330, L = 190, R = 60, T = 10, B = 30;
  const max = base === 10 ? 2.5 : 10, step = base === 10 ? 0.5 : 2;
  const rh = (H - T - B) / data.streams.length;
  const x = (v: number) => L + ((W - L - R) * v) / max;
  const ticks = Array.from({ length: Math.round(max / step) + 1 }, (_, k) => +(k * step).toFixed(1));
  return (
    <div className="lib-chart" ref={box}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "space-between", alignItems: "center" }}>
        <h4>
          {t("library.money.title")} <span className="est">{t("library.est")}</span>
        </h4>
        <div className="lib-seg" role="group" aria-label={t("library.money.baseLabel")}>
          <button type="button" aria-pressed={base === 10} onClick={() => setBase(10)}>{t("library.money.base", { count: installers(10) })}</button>
          <button type="button" aria-pressed={base === 50} onClick={() => setBase(50)}>{t("library.money.base", { count: installers(50) })}</button>
        </div>
      </div>
      <p className="cap">{t("library.money.caption")}</p>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t("library.money.aria")}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={x(v)} x2={x(v)} y1={T} y2={H - B} stroke="var(--lib-grid)" />
            <text x={x(v)} y={H - 10} textAnchor="middle">{m$(v)}</text>
          </g>
        ))}
        {data.streams.map((m, i) => {
          const [lo, hi] = base === 10 ? m.k10 : m.k50;
          const cy = T + rh * i + rh / 2;
          return (
            <g key={m.label}>
              <text x={L - 10} y={cy + 4} textAnchor="end" style={{ fill: "var(--text)" }}>{m.label}</text>
              <rect x={x(lo)} y={cy - 8} width={Math.max(4, x(hi) - x(lo))} height={16} rx={4} fill={i === 3 ? "var(--lib-s2)" : "var(--lib-s1)"} />
              <text x={x(hi) + 6} y={cy + 4}>{t("library.money.range", { lo: m$(lo), hi: m$(hi) })}</text>
              <rect
                x={L} y={cy - rh / 2} width={W - L - R} height={rh} fill="transparent"
                onPointerMove={(ev) => show(ev.currentTarget.ownerSVGElement!, W, H, (x(lo) + x(hi)) / 2, cy - 8, (
                  <><b>{m.label}</b><div>{t("library.money.tip", { count: installers(base), lo: m$(lo), hi: m$(hi) })}</div></>
                ))}
                onPointerLeave={hide}
              />
            </g>
          );
        })}
      </svg>
      {node}
    </div>
  );
}
